import { useState, useEffect } from 'react';

interface TypewriterOptions {
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseTime?: number;
  switchInterval?: number;
  minSubIndex?: number;
}

export const useTypewriter = (
  words: string[],
  options: TypewriterOptions = {}
) => {
  const {
    typingSpeed = 100,
    deletingSpeed = 50,
    pauseTime = 2000,
    switchInterval = 500,
    minSubIndex = 0,
  } = options;

  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [blink, setBlink] = useState(true);

  // Reset when words change (e.g. language switch)
  useEffect(() => {
    setIndex(0);
    setSubIndex(0);
    setIsDeleting(false);
  }, [words]);

  // Blinking cursor logic
  useEffect(() => {
    const timeout2 = setTimeout(() => {
      setBlink((prev) => !prev);
    }, 500);
    return () => clearTimeout(timeout2);
  }, [blink]);

  // Main typewriter logic
  useEffect(() => {
    if (words.length === 0) return;

    const currentWord = words[index] || "";

    // 1. Finished typing: wait then start deleting
    if (!isDeleting && subIndex === currentWord.length) {
      const timeout = setTimeout(() => {
        setIsDeleting(true);
      }, pauseTime);
      return () => clearTimeout(timeout);
    }

    // 2. Finished deleting: switch word immediately then wait
    if (isDeleting && subIndex <= minSubIndex) {
      const timeout = setTimeout(() => {
        setIsDeleting(false);
        setIndex((prev) => (prev + 1) % words.length);
      }, switchInterval);
      return () => clearTimeout(timeout);
    }

    // 3. Typing or Deleting in progress
    const timeout = setTimeout(() => {
      setSubIndex((prev) => {
        if (isDeleting) {
          return Math.max(minSubIndex, prev - 1);
        } else {
          return Math.min(currentWord.length, prev + 1);
        }
      });
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [subIndex, index, isDeleting, words, typingSpeed, deletingSpeed, pauseTime, switchInterval, minSubIndex]);

  const currentText = words[index] ? words[index].substring(0, subIndex) : "";
  
  return `${currentText}${blink ? "|" : " "}`;
};
